import { useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { Modal } from '../../../components/ui/Modal';
import { hasPermission } from '../../../core/permissions';
import {
  QUICK_SOLDIER_STATUSES,
  isQuickStatusCurrent,
  quickStatusPatch,
} from '../../../core/quick-status';
import type { QuickSoldierStatus } from '../../../core/quick-status';
import {
  describeSoldierStatus,
  formatStatusDeadline,
  nextMorningDeadline,
} from '../../../core/soldier-status';
import { updateSoldier } from '../../../db/fleet-store';
import type { Soldier } from '../../../types/fleet';
import type { User } from '../../../types/user';
import { PermissionWall } from './SoldierFormModal';

interface SoldierQuickStatusSheetProps {
  user: User;
  soldier: Soldier;
  /** Evaluation moment; injected so the sheet can be driven at a fixed time. */
  now?: number;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
  /** Opens the full status dialog, for a deadline other than the default. */
  onAdvanced?: (soldier: Soldier) => void;
}

/**
 * "שינוי סטטוס מהיר" — the sheet the status badge on a roster row opens.
 *
 * One tap is the whole interaction: pick a word, the record is written and the
 * sheet closes. There is no save button and no form, which is the point — a
 * manager at the gate says "בבית" and moves on.
 *
 * What each word writes is decided in `src/core/quick-status.ts` and nowhere
 * else, so the sheet has no opinion about availabilities or deadlines: a timed
 * status runs to the next 08:00, and "עוד אפשרויות" opens the full dialog for
 * anything else.
 *
 * "ללא מועד סיום" is the one switch on the sheet. With it on, the word that is
 * tapped is written without any deadline and stays in force until a commander
 * changes it — for the soldier who is בגימלים and nobody knows until when.
 */
export function SoldierQuickStatusSheet({
  user,
  soldier,
  now = Date.now(),
  onClose,
  onSaved,
  onAdvanced,
}: SoldierQuickStatusSheetProps) {
  const canManage = hasPermission(user, 'MANAGE_SOLDIERS');
  /** The option being written, so the row that was tapped is the one that waits. */
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Whether the next tap is written without an end date at all. */
  const [indefinite, setIndefinite] = useState(false);

  if (!canManage) return <PermissionWall onClose={onClose} />;

  const deadline = formatStatusDeadline(nextMorningDeadline(now), now);

  const apply = async (option: QuickSoldierStatus) => {
    setSaving(option.id);
    setError(null);
    try {
      await updateSoldier(user, soldier.id, quickStatusPatch(soldier, option, now, { indefinite }));
      await onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(null);
    }
  };

  return (
    <Modal
      title={`סטטוס מהיר — ${soldier.fullName}`}
      subtitle={describeSoldierStatus(soldier, now)}
      onClose={onClose}
    >
      <div className="space-y-3">
        <div role="radiogroup" aria-label="בחירת סטטוס" className="grid grid-cols-2 gap-2">
          {QUICK_SOLDIER_STATUSES.map((option) => {
            const current = isQuickStatusCurrent(soldier, option, now);
            return (
              <button
                key={option.id}
                type="button"
                role="radio"
                aria-checked={current}
                data-quick-option={option.id}
                disabled={saving !== null}
                onClick={() => void apply(option)}
                className={[
                  'h-12 px-3 rounded-xl border text-sm font-medium transition-colors',
                  'flex items-center gap-2 disabled:opacity-40',
                  current
                    ? 'border-brand-500 bg-brand-50 text-brand-700'
                    : 'border-line bg-panel text-ink/70 hover:text-ink',
                ].join(' ')}
              >
                <span aria-hidden className="text-base">
                  {option.icon}
                </span>
                <span className="truncate">{saving === option.id ? 'שומר…' : option.label}</span>
              </button>
            );
          })}
        </div>

        <label className="flex items-center gap-2 rounded-xl border border-line bg-canvas px-3 h-11 cursor-pointer">
          <input
            type="checkbox"
            data-indefinite-toggle
            checked={indefinite}
            onChange={(event) => setIndefinite(event.target.checked)}
            className="w-4 h-4 accent-brand-500"
          />
          <span className="text-sm">ללא מועד סיום — עד לשינוי ידני</span>
        </label>

        <p className="text-xs text-ink/50">
          {indefinite
            ? 'הסטטוס שייבחר יישמר ללא מועד סיום ויישאר בתוקף עד שמפקד ישנה אותו. "זמין" מנקה כל סטטוס זמני פעיל.'
            : `סטטוס זמני נקבע כברירת מחדל עד ${deadline} ומתפוגג לבדו. "זמין" מנקה כל סטטוס זמני פעיל.`}
        </p>

        {onAdvanced ? (
          <button
            type="button"
            onClick={() => onAdvanced(soldier)}
            className="w-full h-11 rounded-xl border border-line text-sm text-ink/70 hover:text-ink flex items-center justify-center gap-2"
          >
            <SlidersHorizontal size={16} aria-hidden />
            עוד אפשרויות — מועד סיום והערה
          </button>
        ) : null}

        {error ? (
          <p className="text-sm rounded-xl border border-rose-200 bg-rose-50 text-rose-700 p-3">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
